import React, {useMemo} from 'react';
import {
    asArray,
    evaluateFunction,
    HtmlObjectResizeMode,
    stringIsNotNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import {ButtonType, Icon, ListView, listViewCreateDataSource, ListViewItemRoot} from '@wix/devzai-utils-react';
import IconChecked from './icons/checked.svg';
import {
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeMembership,
    doppeActionTypeMembershipPlanResolveSettingsFromBoundEntity,
    doppeActionTypeMembershipResolveTypeFromUrl
} from '../doppe-action-type-membership';
import {
    DoppeImageIconDeviantArtColored,
    DoppeImageIconMemberfulColored,
    DoppeImageIconPatreonColored,
    DoppeImageIconTwitchColored,
    DoppeImageIconTwitterColored,
    DoppeImageIconYouTubeColored
} from '../../../doppe-static-assets/doppe-static-assets';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {FlexLayout} from '@wix/devzai-common-client';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    CouponSourceType,
    DoppeActionTypeCouponView
} from '../../../components/doppe-action-type-coupon-view/doppe-action-type-coupon-view';
import {classes, style} from './doppe-action-type-membership-widget.st.css';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    doppeActionPriceDataGetActualPrice,
    doppeActionPriceDataGetDiscountPrice,
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalMembershipPlanProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";

export const DoppeActionTypeMembershipWidget = React.memo(function DoppeActionTypeMembershipWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeMembership.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const originalMembershipType = actionContext.action.settings.membershipType;
    const membershipLink = actionContext.action.settings.membershipPageLink;

    const icon = useMemo(() => {
        const membershipType = doppeActionTypeMembershipResolveTypeFromUrl(membershipLink);

        if (membershipType === DoppeActionTypeMembership.MembershipType.Other && originalMembershipType === DoppeActionTypeMembership.MembershipType.Memberful) {
            return DoppeImageIconMemberfulColored;
        }

        switch (membershipType) {
            case DoppeActionTypeMembership.MembershipType.Patreon:
                return DoppeImageIconPatreonColored;
            case DoppeActionTypeMembership.MembershipType.Deviantart:
                return DoppeImageIconDeviantArtColored;
            case DoppeActionTypeMembership.MembershipType.Youtube:
                return DoppeImageIconYouTubeColored;
            case DoppeActionTypeMembership.MembershipType.Twitter:
                return DoppeImageIconTwitterColored;
            case DoppeActionTypeMembership.MembershipType.Twitch:
                return DoppeImageIconTwitchColored;
            case DoppeActionTypeMembership.MembershipType.Other:
                return undefined;
            default:
                return DoppeImageIconPatreonColored;
        }
    }, [originalMembershipType, membershipLink]);

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
            icon={icon}
        >
            <DoppeActionTypeMembershipBindableWidget
                actionId={actionContext.action.id}
                reportGoalAchieved={() => {
                    actionContext.reportGoalAchieved();
                }}
                actionSettings={actionContext.action.settings}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeMembershipBindableWidget = React.memo(function DoppeActionTypeMembershipBindableWidget(
    props: {
        actionSettings: DoppeActionTypeMembership.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();
    const {
        membershipProvider,
        membershipProviderId
    } = actionSettings;

    if (membershipProvider !== null && membershipProviderId !== null) {
        const boundProduct = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalMembershipPlanProviderResolveBusinessEntityReference(membershipProvider, membershipProviderId))

        if (!boundProduct || doppeBusinessEntityDtoIsNotFound(boundProduct)) {
            return (
                <DoppeActionTypeMembershipWidgetContentUnavailable
                    reason={
                        !boundProduct ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            return (
                <DoppeActionTypeMembershipWidgetInner
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={doppeActionTypeMembershipPlanResolveSettingsFromBoundEntity(actionSettings, boundProduct)}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeMembershipWidgetInner
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
            />
        )
    }
});


export const DoppeActionTypeMembershipWidgetInner = React.memo(function DoppeActionTypeMembershipWidgetInner(
    props: {
        actionSettings: DoppeActionTypeMembership.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId
    } = props;

    const {
        linkButtonText,
        membershipPageLink,
        priceData,
        membershipImages,
        membershipTitle,
        membershipBenefits,
        membershipDescription,
        couponCode,
    } = actionSettings;

    const title = doppeHideableValueGetVisibleValue(membershipTitle, '');
    const description = doppeHideableValueGetVisibleValue(membershipDescription, '');


    const tierItemsDataSource = useMemo(() => {

        return listViewCreateDataSource<string>(membershipBenefits, {
            idFunction: (_item, index) => index
        })
    }, [membershipBenefits]);

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapper
            mainMedia={asArray(membershipImages)[0] ?
                <DoppePageMediaView
                    className={style(classes.productMedia, {})}
                    mediaResource={asArray(membershipImages)[0]}
                    layoutSpec={{autoHeight: true, maxHeight: 360}}
                    widthSpec={{contentRelativeWidth: 1}}
                    mediaResizeMode={HtmlObjectResizeMode.Cover}
                />
                : null}
            footer={
                <DoppeActionTypeWidgetInnerButton
                    className={style(classes.linkButton)}
                    buttonType={ButtonType.NormalButton}
                    tagName={'a'}
                    href={stringIsNotNullableOrWhiteSpace(membershipPageLink) ? normalizeLinkUrlWithUtmSrcParam(membershipPageLink) : 'about:blank'}
                    target={'_blank'}
                    onClick={() => {
                        reportGoalAchieved();
                    }}
                >
                    {linkButtonText}
                </DoppeActionTypeWidgetInnerButton>
            }
        >
            {title !== '' || description !== '' ? (
                <div
                    className={style(classes.productNameAndDescription, {})}
                >
                    <DoppeActionTypeWidgetInnerTitle
                        className={style(classes.productName, {})}
                    >
                        {membershipTitle}
                    </DoppeActionTypeWidgetInnerTitle>
                    <DoppeActionTypeWidgetInnerDescription
                        className={style(classes.productDescription, {})}
                    >
                        {membershipDescription}
                    </DoppeActionTypeWidgetInnerDescription>
                </div>
            ) : null}
            {evaluateFunction(() => {

                if (tierItemsDataSource.length > 0) {
                    return (
                        <ListView
                            className={style(classes.list)}
                            renderItem={renderProps => {
                                const {
                                    dataItem
                                } = renderProps;

                                return (
                                    <ListViewItemRoot
                                        className={style(classes.listItem, {})}
                                    >
                                        <Icon
                                            className={style(classes.listItemCheckIcon, {})}
                                            icon={IconChecked}
                                        />
                                        <div
                                            className={style(classes.listItemText, {})}
                                        >
                                            {dataItem}
                                        </div>

                                    </ListViewItemRoot>
                                )
                            }}
                        >
                            {tierItemsDataSource}
                        </ListView>
                    )
                } else {
                    return null;
                }
            })}
            {!doppeHideableValueIsHidden(priceData) ? evaluateFunction(() => {
                const actualPrice = doppeActionPriceDataGetActualPrice(priceData);
                const discountPrice = doppeActionPriceDataGetDiscountPrice(priceData);

                if (!actualPrice && !discountPrice) {
                    return null;
                }

                return (
                    <div
                        className={style(classes.priceContainer, {
                            discount: discountPrice !== undefined
                        }, FlexLayout.row.alignToTop)}
                    >
                        {actualPrice ? (
                            <div
                                className={style(classes.actualPrice, {})}
                            >
                                {actualPrice}
                            </div>
                        ) : null}
                        {discountPrice ? (
                            <div
                                className={style(classes.discountPrice, {})}
                            >
                                {discountPrice}
                            </div>
                        ) : null}
                    </div>
                )
            }) : null}
            {
                !doppeHideableValueIsHidden(couponCode) ?
                    <DoppeActionTypeCouponView
                        className={style(classes.couponView, {})}
                        coupon={couponCode}
                        couponSourceType={CouponSourceType.Action}
                        actionId={actionId}
                    />
                    :
                    null
            }
        </DoppeActionTypeWidgetContentLayoutNewWrapper>
    );
});

export const DoppeActionTypeMembershipWidgetContentUnavailable = React.memo(function DoppeActionTypeMembershipWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitlePlan']}
        />
    )
});


function normalizeLinkUrlWithUtmSrcParam(link: string) {
    const utmSrcString = `utm_source=hopp`;

    if (link.indexOf('?') > 0) {
        if (link.indexOf('utm_source') > 0) {
            return link;
        } else {
            return link + `&${utmSrcString}`
        }
    } else {
        return link + `?${utmSrcString}`
    }
}